/* eslint-disable ember/no-classic-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tagName } from '@ember-decorators/component';

// TinyMCE reformats HTML on setContent() at init (e.g. wrapping bare text in
// <p>, collapsing whitespace). ember-tinymce's contentChanged fires a change
// event for that which is not a real user edit — forwarding it would save the
// normalized HTML back over the stored field on mere click-to-edit. We drop
// any onValueChanged until the user actually interacts with the editor.
// Note: intentionally excludes ExecCommand — TinyMCE fires that internally
// during init (e.g. mceSetContent when the initial value is loaded), which
// would trip the flag before the normalization-change event arrives and
// defeat the whole point of this guard.
const USER_INTERACTION_EVENTS =
  'keydown keypress input paste cut drop mousedown';

@tagName('')
export default class ClickToEditTinymce extends Component {
  @service
  clickToEditCoordinator;

  // passed in
  @tracked options = null;

  @tracked value = null;
  onValueChanged = null;
  placeholder = 'Click to write…';

  // state
  @tracked
  editing = false;

  @tracked
  editorReady = false;

  get mergedOptions() {
    let original = this.options || {};
    let userSetup = original.setup;
    let self = this;
    return Object.assign({}, original, {
      auto_focus: true,
      setup(editor) {
        self._editorInstance = editor;
        // Keep refreshing the snapshot on every SetContent until the user
        // actually interacts — TinyMCE fires a SetContent during early init
        // with empty content, then ember-tinymce's init_instance_callback
        // fires another SetContent with the real stored value. We want the
        // latter. Once the user has interacted, we lock the snapshot.
        editor.on('init', () => {
          if (!self.isDestroying && !self.isDestroyed) {
            self.set('editorReady', true);
          }
        });
        editor.on('SetContent', () => {
          if (self._userHasInteracted) return;
          self._initialNormalizedContent = editor.getContent();
        });
        editor.on(USER_INTERACTION_EVENTS, () => {
          self._userHasInteracted = true;
        });
        if (userSetup) {
          userSetup(editor);
        }
      },
    });
  }

  get hasContent() {
    let v = this.value || '';
    return v.replace(/<[^>]*>/g, '').trim().length > 0;
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.clickToEditCoordinator.deregister(this);
  }

  // Called by the coordinator when another slot is taking over. Synchronously
  // commits any keystrokes that were still within ember-tinymce's 10ms
  // debounce window before the editor tears down, then flips back to preview.
  deactivate() {
    if (!this.editing) return;
    let editor = this._editorInstance;
    if (editor && !editor.removed) {
      this._emit(editor.getContent());
    }
    this._finalized = true;
    this.editing = false;
  }

  _resetEditState() {
    this._userHasInteracted = false;
    this._initialNormalizedContent = null;
    this._lastSentValue = null;
    this._finalized = false;
    this._editorInstance = null;
    this.editorReady = false;
  }

  // Forward a new editor value to the parent, skipping anything we can detect
  // as a non-edit: pre-interaction change events (TinyMCE normalizing stored
  // HTML on setContent), an exact match of the post-init normalized content,
  // or a repeat of what we most recently forwarded. @value from the parent
  // doesn't update reactively (the reporting/get-section-field helper caches
  // on argument identity), so _lastSentValue is our own record of the
  // last-sent string.
  _emit(value) {
    if (this._finalized) return;
    if (!this._userHasInteracted) return;
    if (value === this._initialNormalizedContent) return;
    if (value === this._lastSentValue) return;
    this._lastSentValue = value;
    if (this.onValueChanged) {
      this.onValueChanged(value);
    }
  }

  @action
  startEditing() {
    if (this.editing) return;
    this._resetEditState();
    this.clickToEditCoordinator.activate(this);
    this.editing = true;
  }

  @action
  handleValueChanged(value) {
    this._emit(value);
  }
}
